import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, map, of } from 'rxjs';
import { FetchTotalWealth, UpdateTotalWealthFlag } from '../actions/total-wealth.action';
import { AccountService } from '../services/account-service/account-service.service';

export interface TotalWealthResult {
  value: string | null;
  currency: string;
  fetching: boolean;
}

export interface TotalWealthStateModel {
  accountContextWidget: {
    enableTotalWealth: boolean;
  };
  results: {
    [currency: string]: TotalWealthResult;
  };
}

@State<TotalWealthStateModel>({
  name: 'totalWealth',
  defaults: {
    accountContextWidget: {
      enableTotalWealth: false,
    },
    results: {},
  },
})
@Injectable()
export class TotalWealthState {
  constructor(private accountService: AccountService) {}

  @Selector()
  static getTotalWealthState(state: TotalWealthStateModel) {
    return state;
  }

  static resultForCurrency(currency: string) {
    return (state: TotalWealthStateModel): TotalWealthResult | undefined => state.results?.[currency];
  }

  @Action(FetchTotalWealth)
  fetchTotalWealth(
    ctx: StateContext<TotalWealthStateModel>,
    { currency, enableTotalWealth }: FetchTotalWealth,
  ) {
    const patchTotalWealthValue = (value: string | null, fetching: boolean) => {
      const state = ctx.getState();

      ctx.patchState({
        accountContextWidget: {
          ...state.accountContextWidget,
          enableTotalWealth:
            enableTotalWealth !== undefined
              ? Boolean(enableTotalWealth)
              : state.accountContextWidget?.enableTotalWealth,
        },
        results: {
          ...state.results,
          [currency]: { value, currency, fetching },
        },
      });
    };

    // mark fetching
    patchTotalWealthValue(ctx.getState().results[currency]?.value ?? null, true);

    return this.accountService.getTotalWealth(currency).pipe(
      map((response) => {
        patchTotalWealthValue(response?.totalWealth ?? null, false);
      }),
      catchError(() => {
        patchTotalWealthValue(null, false);
        return of(null);
      }),
    );
  }

  @Action(UpdateTotalWealthFlag)
  updateTotalWealthFlag(ctx: StateContext<TotalWealthStateModel>, action: UpdateTotalWealthFlag) {
    ctx.patchState({
      ...ctx.getState(),
      accountContextWidget: {
        ...ctx.getState()?.accountContextWidget,
        enableTotalWealth: action.enableTotalWealth,
      },
    });
  }
}
